import type { Ref } from 'vue';

// Numeric levels are intentionally aligned with the legacy `MenuLevel` enum
// from `useDragToBottom` so consumers (notably `filterSlugs.vue` and
// `PropertyListSection`) keep working with raw integers:
//   0 = CLOSED    → drawer fully covers viewport (map hidden, list visible)
//   1 = HALF_OPEN → drawer at `snapHeights[1]` px visible from bottom
//   2 = FULLY_OPEN→ drawer minimized to peek = `snapHeights[2]` px visible
//
// Using a `const` object + value-type union (instead of `enum`) follows the
// project convention of preferring maps over enums for safer narrowing and
// no runtime emit of an extra object wrapper.
export const SheetLevel = {
	CLOSED: 0,
	HALF_OPEN: 1,
	FULLY_OPEN: 2
} as const;

export type SheetLevel = (typeof SheetLevel)[keyof typeof SheetLevel];

export interface SheetSnapHeights {
	1: number;
	2: number;
}

export interface BottomSheetConfig {
	snapHeights: Ref<SheetSnapHeights>;
	// Fixed chrome (navbar + filters) height at top of viewport. The page
	// reserves this as padding-top; together with the spacer it anchors peek.
	topChromeHeight?: number;
	// Chrome visible above the sheet at CLOSED (filters only — navbar stays
	// hidden there). Defaults to topChromeHeight.
	closedTopChromeHeight?: number;
	// Elemento do spacer: permite a barreira sincronizar a altura dele com o
	// scrollTo no MESMO commit ao trocar de nivel (sem flick em repouso).
	spacerElement?: Ref<HTMLElement | null>;
	breakpoint?: number;
	disabled?: Ref<boolean>;
}

export interface BottomSheetController {
	onSheetTouchStart: (event: TouchEvent) => void;
	onHandleTouchStart: (event: TouchEvent) => void;
	isScreenBelowBreakpoint: Ref<boolean>;
	spacerHeight: Ref<number>;
	menuBottomLevel: Ref<SheetLevel>;
	currentY: Ref<number>;
	isDragging: Ref<boolean>;
	isTransitioning: Ref<boolean>;
	// True enquanto o sheet cobre a viewport (em repouso no CLOSED ou alem, na
	// leitura). Fica false assim que a descida comeca — ao contrario de
	// `menuBottomLevel`, que so troca no meio do caminho (snap mais proximo).
	isSheetFullscreen: Ref<boolean>;
	moveBackOneLevel: () => void;
	moveBackToInitialPosition: () => void;
	toggleToClosedWhenOpen: () => void;
	toggleToHalfOpenWhenClosed: () => void;
	scrollToLevel: (level: SheetLevel, instant?: boolean) => void;
	shouldIgnoreHandleClick: () => boolean;
}
